import config from 'src/config'
import { post } from 'src/functions/fetch'
import {
  getCartPriceAmount,
  isCartPriceLockValid,
} from 'src/functions/cartPriceLock'
import {
  isProductCommercePreviews,
  tProductCommercePreviews,
} from 'src/shared/frontendTypes/productCommerce'
import { discount, discountPrice } from 'src/shared/frontendTypes/product'
import { isItem as isWarenkorbItem, orderItem, tItem, tItems } from 'src/shared/frontendTypes/warenkorb'

const keyOf = (id: number, key: string) => `${id}:${key}`
type CartPriceOptions = NonNullable<Parameters<typeof getCartPriceAmount>[2]>

type RefreshableCartItem = orderItem & { hash?: string }

const COMMERCE_PREVIEW_CACHE_MS = 10000

const commercePreviewCache = new Map<string, {
  expiresAt: number
  promise: Promise<tProductCommercePreviews | null>
}>()

const isRefreshableCartItem = (item: unknown): item is RefreshableCartItem => {
  if (!item || typeof item !== 'object') return false

  return (
    'id' in item
    && typeof item.id === 'number'
    && 'key' in item
    && typeof item.key === 'string'
  )
}

const toDiscountPrice = (price: number, appliedDiscount?: discount): number | discountPrice =>
  appliedDiscount
    ? {
      regularPrice: price,
      discountPrice: appliedDiscount.price,
      discountPriceAfterStock: appliedDiscount.priceAfterStock,
      stockDiscountLimit: appliedDiscount.stockDiscountLimit,
      limitStockDiscountToStock: appliedDiscount.limitStockDiscountToStock,
      quantityDiscount: appliedDiscount.quantityDiscount,
      title: appliedDiscount.metaTitle || '',
      type: appliedDiscount.type || 'invisible',
      countdownVariant: appliedDiscount.countdownVariant,
      dateEnd: appliedDiscount.dateEnd,
      showPercent: appliedDiscount.showPercent,
    }
      : price

const getCommercePreviewRequestKey = (items: RefreshableCartItem[]) => JSON.stringify(
  items
    .map(({ id, key }) => ({ id, key }))
    .sort((a, b) => a.id - b.id || a.key.localeCompare(b.key)),
)

type LiveCommerce = {
  brand?: number
  brandTitel?: string
  categorie?: number
  price: number
  deliverytime: number
  stockDeliverytime?: number
  stock?: number
  shippingType: number
  hash?: string
  discount?: discount
  priceLock?: orderItem['priceLock']
  purchaseMode?: orderItem['purchaseMode']
  inquireOnly?: true
}

const getBestLockedPrice = (
  itemPriceLock: orderItem['priceLock'],
  livePriceLock: orderItem['priceLock'],
  now: Date,
  qty: number,
  cartPriceOptions: CartPriceOptions = {},
) => {
  const locks = [
    { lock: livePriceLock, priority: 0 },
    { lock: itemPriceLock, priority: 1 },
  ]
    .filter((entry): entry is {
      lock: NonNullable<orderItem['priceLock']>
      priority: number
    } =>
      isCartPriceLockValid(entry.lock, now),
    )

  return locks.sort((a, b) =>
    getCartPriceAmount(a.lock.price, qty, cartPriceOptions)
      - getCartPriceAmount(b.lock.price, qty, cartPriceOptions)
    || a.priority - b.priority
  )[0]?.lock
}

const mergeItemCommerce = (item: RefreshableCartItem, live?: LiveCommerce) => {
  const now = new Date()
  const livePrice = live ? toDiscountPrice(live.price, live.discount) : undefined
  const livePriceLock = isCartPriceLockValid(live?.priceLock, now)
    ? live?.priceLock
    : undefined
  const effectiveStock = typeof live?.stock === 'number' ? live.stock : item.stock
  const cartPriceOptions = {
    onlyStock: item.onlyStock,
    stock: effectiveStock,
  }
  const priceLock = getBestLockedPrice(
    item.priceLock,
    livePriceLock,
    now,
    item.qty,
    cartPriceOptions,
  )
  const baselinePrice = livePrice ?? item.price

  if (
    priceLock
    && getCartPriceAmount(priceLock.price, item.qty, cartPriceOptions)
      <= getCartPriceAmount(baselinePrice, item.qty, cartPriceOptions)
  ) {
    return {
      ...item,
      brand: typeof live?.brand === 'number'
        ? { id: live.brand, titel: live.brandTitel || item.brand?.titel || '' }
        : item.brand,
      categorie: live?.categorie ?? item.categorie,
      price: priceLock.price,
      priceLock,
      deliverytime: live?.deliverytime ?? item.deliverytime,
      stockDeliverytime: live?.stockDeliverytime ?? item.stockDeliverytime,
      stock: typeof live?.stock === 'number' ? live.stock : item.stock,
      shippingType: live?.shippingType ?? item.shippingType,
      hash: live?.hash ?? item.hash,
      purchaseMode: live ? live.purchaseMode ?? 'cart' : item.purchaseMode,
      inquireOnly: live ? live.inquireOnly : item.inquireOnly,
    }
  }

  return {
    ...item,
    brand: typeof live?.brand === 'number'
      ? { id: live.brand, titel: live.brandTitel || item.brand?.titel || '' }
      : item.brand,
    categorie: live?.categorie ?? item.categorie,
    price: baselinePrice,
    priceLock: livePriceLock,
    deliverytime: live?.deliverytime ?? item.deliverytime,
    stockDeliverytime: live?.stockDeliverytime ?? item.stockDeliverytime,
    stock: typeof live?.stock === 'number' ? live.stock : item.stock,
    shippingType: live?.shippingType ?? item.shippingType,
    hash: live?.hash ?? item.hash,
    purchaseMode: live ? live.purchaseMode ?? 'cart' : item.purchaseMode,
    inquireOnly: live ? live.inquireOnly : item.inquireOnly,
  }
}

const fetchCommercePreviews = async (
  validItems: RefreshableCartItem[],
): Promise<tProductCommercePreviews | null> => {
  try {
    const response = await post({
      url: 'product/previews/commerce',
      body: {
        ...config.shop,
        products: validItems.map(({ id, key }) => ({ id, key })),
      },
    })

    return isProductCommercePreviews(response) ? response : null
  } catch (_error) {
    return null
  }
}

const getCommercePreviews = (
  validItems: RefreshableCartItem[],
): Promise<tProductCommercePreviews | null> => {
  const requestKey = getCommercePreviewRequestKey(validItems)
  const now = Date.now()
  const cached = commercePreviewCache.get(requestKey)

  if (cached && cached.expiresAt > now) return cached.promise

  const promise = fetchCommercePreviews(validItems)
  commercePreviewCache.set(requestKey, {
    expiresAt: now + COMMERCE_PREVIEW_CACHE_MS,
    promise,
  })

  return promise
}

export const refreshCartItemsListCommerce = async (
  items: RefreshableCartItem[],
): Promise<RefreshableCartItem[]> => {
  const validItems = items.filter(isRefreshableCartItem)
  if (validItems.length === 0) return items

  const previews = await getCommercePreviews(validItems)
  if (!previews) return items

  const byKey = new Map(previews.map(preview => [keyOf(preview.id, preview.key), preview]))

  return items.map(item =>
    isRefreshableCartItem(item)
      ? mergeItemCommerce(item, byKey.get(keyOf(item.id, item.key)))
      : item,
  )
}

export const refreshCartItemsCommerce = async (items: tItems): Promise<tItems> => {
  const refreshedItems = await refreshCartItemsListCommerce(Object.values(items))

  return refreshedItems.reduce<tItems>((acc, item) => {
    if (!isWarenkorbItem(item)) {
      if (!isRefreshableCartItem(item)) return acc
      acc[item.key] = item as tItem
      return acc
    }

    acc[item.key] = item
    return acc
  }, {})
}
